import type {
  CreateGuestBookingRequest,
  GuestBookingAvailability,
  GuestBookingAvailabilityResponse,
  GuestBookingQuote,
  GuestBookingQuoteResponse,
  GuestBookingRequestReceipt,
  GuestBookingRequestResponse,
  GuestBookingSelection,
  GuestDeskMapAvailability,
  GuestDeskMapAvailabilityResponse,
  GuestSpaceDetail,
  GuestSpaceListResponse,
  GuestSpaceResponse,
  GuestSpaceSummary,
} from '@mds/shared/types/guest-booking';

import { api } from '@/lib/api';

/** The public facility endpoints — see `backend/docs/guest-bookings.md`. */

/**
 * Active space cards. Packages are `features/packages`, and appear inline on floor details.
 *
 * There is deliberately no lookup, reschedule or cancellation route on the
 * public API: once a request is in, it is the admin workflow's.
 */
export const listSpaces = (): Promise<GuestSpaceSummary[]> =>
  api.get<GuestSpaceListResponse>('spaces').then((r) => r.data);

export const getSpace = (slug: string): Promise<GuestSpaceDetail> =>
  api.get<GuestSpaceResponse>(`spaces/${slug}`).then((r) => r.data);

/**
 * One space's board for one date: desks on a floor, whole hours in a room.
 *
 * Privacy-safe by construction — who holds a desk never crosses the wire, only
 * whether it is free. Read it again before every attempt: an unpaid request
 * does not hold anything, so a board can only ever be a snapshot.
 */
export const getAvailability = (
  resourceId: string,
  date: string,
): Promise<GuestBookingAvailability> =>
  api
    .get<GuestBookingAvailabilityResponse>(`spaces/${resourceId}/availability`, {
      query: { date },
    })
    .then((r) => r.data);

/** Minimal per-desk state for painting the public floor maps. */
export const getDeskAvailability = (
  resourceId: string,
  startDate: string,
  deskPackageItemId?: string,
  startMinute?: number,
  endMinute?: number,
): Promise<GuestDeskMapAvailability[]> =>
  api
    .get<GuestDeskMapAvailabilityResponse>(`spaces/${resourceId}/desks/availability`, {
      query: { startDate, deskPackageItemId, startMinute, endMinute },
    })
    .then((r) => r.data);

/**
 * Prices a selection. The server owns the number — the client never calculates
 * or submits one of its own.
 */
export const getQuote = (selection: GuestBookingSelection): Promise<GuestBookingQuote> =>
  api.post<GuestBookingQuoteResponse>('booking-quotes', selection).then((r) => r.data);

/**
 * Submits the request. `requestId` makes a retry idempotent: generate one when
 * the visitor starts the submission and reuse it only to retry that exact
 * submission, or the server answers `BOOKING_REQUEST_ID_REUSED`.
 *
 * A 409 `BOOKING_QUOTE_CHANGED` carries a fresh quote in `details.quote`; the
 * visitor has to see the new amount and agree to it before this is sent again.
 * A 429 `BOOKING_REQUEST_CONTACT_RATE_LIMITED` means the email or normalized
 * phone number submitted another request during the current public rate-limit window.
 */
export const requestBooking = (
  body: CreateGuestBookingRequest,
): Promise<GuestBookingRequestReceipt> =>
  api.post<GuestBookingRequestResponse>('booking-requests', body).then((r) => r.data);
